import { Window } from "@dotcom-frontend/data";
import { EventEmitter, Inject, Injectable, PLATFORM_ID } from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import { Subject } from "rxjs";

declare const window: Window;

@Injectable({
  providedIn: "root"
})
export class AnalyticsService {
  private formFieldInteractionSource = new Subject<any>();
  formFieldInteraction$ = this.formFieldInteractionSource.asObservable();
  private resetButtonClickedSource = new Subject<void>();
  resetButtonClicked$ = this.resetButtonClickedSource.asObservable();
  public analyticsScriptLoaded$: EventEmitter<any>;
  isBrowser = isPlatformBrowser(this.platformId);
  private formName!: string;

  constructor(@Inject(PLATFORM_ID) private platformId: any) {
    this.analyticsScriptLoaded$ = new EventEmitter();
  }

  initAdobeLaunchDataLayer() {
    (window as Window).adobeDataLayer = (window as Window).adobeDataLayer || [];
  }

  pushToDataLayer(data: any) {
    if (this.isBrowser) {
      if (window && (window as Window).adobeDataLayer) {
        (window as Window).adobeDataLayer.push(data);
      }
    }
  }

  resetButtonClicked() {
    this.resetButtonClickedSource.next();
  }

  setFormName(formName: string) {
    this.formName = formName;
  }

  getFormName(): string | undefined {
    return this.formName;
  }

  adobeInteractionEvent(
    containerName: any,
    clickUrl: any,
    clickName: any,
    interactionRegion: any,
    clickType: any,
    pageName: any,
    interactionDetails: string
  ) {
    const data = {
      event: "interactionClick",
      interactionClicks: {
        value: 1
      },
      interaction: {
        interactionContainer: containerName,
        interactionLink: clickUrl,
        interactionName: clickName,
        interactionRegion: interactionRegion,
        interactionType: clickType,
        interactionPage: pageName,
        interactionDetails: interactionDetails
      }
    };
    this.pushToDataLayer(data);
  }

  recordFilterSelections(filterType: string, filterName: string) {
    const data = {
      event: "filterSelection",
      filterType: filterType,
      filterName: filterName
    };
    this.pushToDataLayer(data);
  }

  adobeDocLibraryInteractionEvent(
    interactionPosition: any,
    containerName: any,
    clickUrl: any,
    clickName: any,
    interactionRegion: any,
    clickType: any,
    pageName: any,
    interactionDetails: string
  ) {
    const data = {
      event: "interactionClick",
      interactionClicks: {
        value: 1
      },
      interaction: {
        interactionContainer: interactionPosition,
        interactionContainerType: containerName,
        interactionLink: clickUrl,
        interactionName: clickName,
        interactionRegion: interactionRegion,
        interactionType: clickType,
        interactionPage: pageName,
        interactionDetails: interactionDetails
      }
    };
    this.pushToDataLayer(data);
  }

  adobeFormInteractionEvent(
    formStep: any,
    formName: any,
    clickName: any,
    pageId: any,
    clickType: string
  ) {
    const data = {
      event: "interactionClick",
      interactionClicks: {
        value: 1
      },
      interaction: {
        interactionContainer: formStep,
        interactionContainerType: formName,
        interactionName: clickName,
        interactionPage: pageId,
        interactionType: clickType
      }
    };
    this.pushToDataLayer(data);
  }

  triggerFormFieldInteraction(
    formInput: string,
    formValue: any,
    formName: string
  ) {
    this.formFieldInteractionSource.next({
      input: formInput,
      value: formValue,
      formName
    });
  }

  formSubmitEvent(formData: any) {
    const data = {
      ...formData,
      event: "interactionClick",
      interactionClicks: {
        value: 1
      }
    };

    this.pushToDataLayer(data);
  }

  formCompleteEvent(formData: any) {
    const data = {
      ...formData,
      event: "formComplete",
      formCompletes: {
        value: 1
      }
    };

    this.pushToDataLayer(data);
  }

  formErrorEvent(formData: any) {
    const data = {
      ...formData,
      event: "formError",
      formErrors: {
        value: 1
      }
    };

    this.pushToDataLayer(data);
  }

  formStartEvent(formData: any) {
    const data = {
      ...formData,
      event: "formStart",
      formStarts: {
        value: 1
      }
    };

    this.pushToDataLayer(data);
  }

  formStepStartEvent(formData: any) {
    const data = {
      ...formData,
      event: "formStepStart",
      formStepStarts: {
        value: 1
      }
    };

    this.pushToDataLayer(data);
  }

  formFieldInteractionEvent(formData: any) {
    const data = {
      ...formData,
      event: "formFieldInteraction",
      formFieldInteractions: {
        value: 1
      }
    };

    this.pushToDataLayer(data);
  }

  scriptLoaded() {
    this.analyticsScriptLoaded$.emit();
  }
}
